import {
  Plus,
  MoreHorizontal,
  Pencil,
  Archive,
  CheckCircle2,
  Tag,
  Lock,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import {
  TagOption,
  TaxonomyStatus,
} from "@/lib/schema";


import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const STATUS_CONFIG: Record<
  TaxonomyStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
};

export interface MoodFeelingCollapsibleProps {
  mood: TagOption;
  feelings: TagOption[];
  onEditMood: (opt: TagOption) => void;
  onEditFeeling: (opt: TagOption) => void;
  onAddFeeling: () => void;
  onArchiveFeeling: (id: string) => void;
  onRestoreFeeling: (id: string) => void;
  isAdmin: boolean;
}

export function MoodFeelingCollapsible({
  mood,
  feelings,
  onEditMood,
  onEditFeeling,
  onAddFeeling,
  onArchiveFeeling,
  onRestoreFeeling,
  isAdmin,
}: MoodFeelingCollapsibleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeFeelings = feelings.filter((f) => f.status === "active");
  const archivedFeelings = feelings.filter((f) => f.status === "archived");

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="rounded-md border"
        data-testid={`mood-section-${mood.id}`}
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 min-w-0 cursor-pointer select-none flex-1">
              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground">
                {mood.label}
              </span>
              <Badge
                variant="default"
                className="text-[10px] no-default-hover-elevate no-default-active-elevate"
              >
                {activeFeelings.length}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {activeFeelings.length} feeling
                {activeFeelings.length !== 1 ? "s" : ""}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </CollapsibleTrigger>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onEditMood(mood);
            }}
            data-testid={`button-edit-mood-${mood.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
        <CollapsibleContent>
          <div className="border-t px-3 pb-3 pt-2 space-y-1.5">
            {activeFeelings.length === 0 && archivedFeelings.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                No feelings assigned to this mood yet.
              </p>
            ) : (
              <>
                {activeFeelings.map((feeling) => (
                  <div
                    key={feeling.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border"
                    data-testid={`feeling-${feeling.id}`}
                  >
                    <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-sm text-foreground flex-1 min-w-0">
                      {feeling.label}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          data-testid={`button-feeling-actions-${feeling.id}`}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onEditFeeling(feeling)}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onArchiveFeeling(feeling.id)}
                              className="text-destructive"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
                {archivedFeelings.length > 0 && (
                  <div className="pt-1">
                    <p className="text-xs text-muted-foreground mb-1">
                      Archived
                    </p>
                    {archivedFeelings.map((feeling) => (
                      <div
                        key={feeling.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border opacity-60"
                        data-testid={`feeling-archived-${feeling.id}`}
                      >
                        <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="text-sm text-foreground flex-1 min-w-0">
                          {feeling.label}
                        </span>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => onRestoreFeeling(feeling.id)}
                            data-testid={`button-restore-feeling-${feeling.id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onAddFeeling}
              className="mt-1.5 w-full"
              data-testid={`button-add-feeling-${mood.id}`}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Feeling
            </Button>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
